import clsx from 'clsx';
import parseHtmlToReact from 'html-react-parser';
import { lazy, memo, Suspense, useMemo, useState } from 'react';

import useAttributeChange from '../hooks';

import { trackCardElement } from '@services/GoogleTagManagerService';
import { hydrateWithTranslations } from '@services/TranslatorService';
import ErrorBoundary from '@shared/ErrorBoundary';
import { useEventbus } from '@shared/hooks/useEventbus';
import { useTranslateFn } from '@shared/Translate';
import type { Match } from '@shared-types/match.types';
import { competitionUrl } from '@utilities/WindowContext';

const StoryWidget = lazy(() => import('@shared/StoryWidget'));

import * as classes from './styles.module.scss';

const LiveComponent = ({
  summary,
  match,
  showAudioDescLink,
}: {
  summary: string;
  match: Match;
  showAudioDescLink?: boolean;
}) => {
  const t = useTranslateFn();
  const variant = useAttributeChange('.js-match-content', 'data-ab-test-variant');

  const [refreshKey, setRefreshKey] = useState(0);
  const [hasError, setHasError] = useState(false);

  const handleStoryError = () => {
    setHasError(true);
  };

  useEventbus(() => {
    setRefreshKey((prevKey) => prevKey + 1);
  }, 'ab-refresh');

  const trackingData = useMemo(() => {
    const { id, homeTeam, awayTeam } = match;

    return {
      name: `Match Summary - ${hydrateWithTranslations(homeTeam)?.displayName}-${
        hydrateWithTranslations(awayTeam)?.displayName
      }`,
      id,
      category: 'match summary',
    };
  }, [match]);

  return (
    <ErrorBoundary>
      <div className={clsx(!hasError && variant === 'B' && classes.matchSummaryContainer)}>
        {!hasError && variant === 'B' && (
          <Suspense>
            <StoryWidget
              categories={[match.id]}
              onError={handleStoryError}
              disablePopover
              seasonYear={match.seasonYear}
              showLiveChip
              showTitle
              height={125}
              width={125}
              fontSizeDesktop={9}
              fontSizeMobile={9}
              liveChipFontSizeDesktop={9}
              liveChipFontSizeMobile={9}
              context={variant}
            />
          </Suspense>
        )}
        <div
          key={refreshKey}
          className={clsx(classes.bullets, !hasError && variant === 'B' && classes.variantB)}
          ref={trackCardElement(trackingData)}
        >
          <div className={classes.title}>{t('match_summary')}</div>

          {showAudioDescLink && (
            <a
              href={`/${competitionUrl}/audio-descriptive-commentary/`}
              aria-label={t('audio_desc_comment')}
              className='hideTitle showOnFocus'
            >
              {t('audio_desc_comment')}
            </a>
          )}

          <div className={classes.list}>{parseHtmlToReact(summary ?? '')}</div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default memo(LiveComponent);
